import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { auth } from '../../utils/firebase/firebase.utils';
import { selectTeamIndex, selectUser, selectEmailVerificationPending } from '../../store/user/user.selector.js';
import { selectDisplayedGradient, selectGradientData } from '../../store/system/system.selector.js';
import { setDisplayedGradient } from '../../store/system/system.action.js';
import { setEmailVerificationPending } from '../../store/user/user.action.js';

// components
import CreateAccount from './components/create-account/CreateAccount';
import CylinderCardBackground from '../../components/cylinder-card-background/CylinderCardBackground.js';
import GradientBG from '../../components/gradient-bg/GradientBG.js';
import SignIn from './components/sign-in/SignIn';
import PrimaryBtn from '../../components/btns/primary-btn/PrimaryBtn.js';
// styles
import {
    AuthPageContainer,
    Content,
    ToggleContainer,
    MainGradientBGContainer,
    VerifyEmailContent,
    VerifyEmailButtons,
    VerifyEmailError,
    VerifyEmailSuccess
} from './AuthPage.styles.js';
import PageNav, { NavButton } from '../../components/system/page-nav/PageNav.js';


export default function AuthPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const displayedGradient = useSelector(selectDisplayedGradient);
    const teamIndex = useSelector(selectTeamIndex);
    const gradientData = useSelector(selectGradientData);
    const user = useSelector(selectUser);
    const emailVerificationPending = useSelector(selectEmailVerificationPending);
    const [authToggle, setAuthToggle] = useState(location.state?.tab === 'createAccount' ? 'createAccount' : 'signIn');

    // Sync tab when navigating from LandingPage with state
    useEffect(() => {
        if (location.state?.tab && !emailVerificationPending) {
            setAuthToggle(location.state.tab);
        }
    }, [location.state?.tab, emailVerificationPending]);
    const [isResending, setIsResending] = useState(false);
    const [resendMessage, setResendMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!displayedGradient) {
            dispatch(setDisplayedGradient('A'));
        }
    }, [dispatch, displayedGradient]);

    // Redirect to auth if user tries to navigate to other routes while email verification is pending
    useEffect(() => {
        if (emailVerificationPending && location.pathname !== '/auth') {
            navigate('/auth', { replace: true });
        }
    }, [emailVerificationPending, location.pathname, navigate]);

    // Email verification handlers
    const handleResendVerification = async () => {
        if (!user) return;

        setIsResending(true);
        // Clear any previous messages
        setError('');
        setResendMessage('');

        try {
            await sendEmailVerification(auth.currentUser);
            setResendMessage('Verification email sent! Please check your inbox.');
            setError(''); // Ensure error is cleared when success is set
        } catch (error) {
            setError('Failed to send verification email. Please try again.');
            setResendMessage(''); // Ensure resendMessage is cleared when error is set
        } finally {
            setIsResending(false);
        }
    };

    // Handle toggle clicks - clear authentication and start fresh
    const handleToggleClick = async (toggle) => {
        if (emailVerificationPending) {
            // Sign out from Firebase to start completely fresh
            try {
                await signOut(auth);
            } catch (error) {
                // Sign out error - ignore
            }
        }
        setAuthToggle(toggle);
        setError('');
        setResendMessage('');
    };

    const handleCheckVerification = async () => {
        // Clear any previous messages
        setError('');
        setResendMessage('');

        try {
            // Reload the user from Firebase to get the latest verification status
            await auth.currentUser?.reload();

            // Check the current Firebase auth state
            if (auth.currentUser?.emailVerified) {
                dispatch(setEmailVerificationPending(false));
                navigate('/', { replace: true });
            } else {
                setError('Email is still not verified. Please check your inbox and click the verification link.');
            }
        } catch (error) {
            setError('Error checking verification status. Please try again.');
        }
    };

    // Determine what to render based on email verification status
    const renderAuthContent = () => {
        return (
            <>
                <ToggleContainer>
                    {[['signIn', 'Sign In'], ['createAccount', 'Create Account']].map(([key, label]) => {
                        const active = authToggle === key && !emailVerificationPending;
                        return (
                            <NavButton
                                key={key}
                                active={active}
                                aria-pressed={active}
                                onClick={() => handleToggleClick(key)}
                            >
                                {label}
                            </NavButton>
                        );
                    })}
                </ToggleContainer>
                {emailVerificationPending ? (
                    <VerifyEmailContent className='panel-bg'>
                        <h2>Email Verification Required</h2>

                        <p>
                            Your email address <strong>{user?.email}</strong> still needs to be verified before you can access the platform.
                        </p>

                        <p>
                            Please check your email inbox and click the verification link we sent you. If you don't see the email, check your spam folder.
                        </p>

                        {error && <VerifyEmailError><h3>{error}</h3></VerifyEmailError>}
                        {resendMessage && <VerifyEmailSuccess><h3>{resendMessage}</h3></VerifyEmailSuccess>}

                        <VerifyEmailButtons>
                            <PrimaryBtn
                                onClick={handleResendVerification}
                                disabled={isResending}
                                title={isResending ? 'SENDING...' : 'RESEND LINK'}
                                width={'fill'}
                            />

                            <PrimaryBtn
                                onClick={handleCheckVerification}
                                title="I'VE VERIFIED"
                                width={'fill'}
                            />
                        </VerifyEmailButtons>
                    </VerifyEmailContent>
                ) : authToggle === 'signIn' ? (
                    <SignIn />
                ) : (
                    <CreateAccount />
                )}
            </>
        );
    };

    return (
        <AuthPageContainer>
            <PageNav />
            <Content>
                <div className='auth-container'>
                    {renderAuthContent()}
                </div>
            </Content>
            <CylinderCardBackground
                numColumns={8}
                cardsPerColumn={8}
                radius={600}
                arcDegrees={180}
                speed={24}
                opacity={0.55}
            />
            <MainGradientBGContainer>
                <GradientBG setGradient={gradientData[teamIndex]} />
            </MainGradientBGContainer>
        </AuthPageContainer>
    )
}
